// src/i18n/index.js
//
// Single i18n instance, lazy-loaded locale bundles. Default lang resolves
// from localStorage → user.locale (later) → navigator.language → 'en'.
//
// Supported locales: English, French, German, Spanish, Japanese, Simplified
// Chinese (zh-Hans). Add another by dropping a JSON next to en.json and
// extending LOCALES.

import { createI18n } from 'vue-i18n';

export const LOCALES = [
  { code: 'en',      label: 'English',     short: 'EN', flag: 'us' },
  { code: 'fr',      label: 'Français',    short: 'FR', flag: 'fr' },
  { code: 'de',      label: 'Deutsch',     short: 'DE', flag: 'de' },
  { code: 'es',      label: 'Español',     short: 'ES', flag: 'es' },
  { code: 'ja',      label: '日本語',       short: 'JA', flag: 'jp' },
  { code: 'zh-Hans', label: '简体中文',     short: 'ZH', flag: 'cn' },
];

const STORAGE_KEY = 'isamurai_locale';

import en from './locales/en.json';

export const i18n = createI18n({
  legacy: false,
  globalInjection: true,
  locale: detectInitialLocale(),
  fallbackLocale: 'en',
  silentTranslationWarn: true,
  silentFallbackWarn: true,
  missingWarn: false,
  fallbackWarn: false,
  messages: { en },
});

function detectInitialLocale() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && LOCALES.some(l => l.code === stored)) return stored;
  } catch (_) {}
  const navLang = (typeof navigator !== 'undefined' && navigator.language) || 'en';
  if (/^zh\b/i.test(navLang)) return 'zh-Hans';
  if (/^ja\b/i.test(navLang)) return 'ja';
  if (/^fr\b/i.test(navLang)) return 'fr';
  if (/^de\b/i.test(navLang)) return 'de';
  if (/^es\b/i.test(navLang)) return 'es';
  return 'en';
}

const _loaded = new Set(['en']);

export async function setI18nLocale(code) {
  const target = code || i18n.global.locale.value;
  if (!LOCALES.some(l => l.code === target)) return;
  if (!_loaded.has(target)) {
    try {
      const mod = await import(/* webpackChunkName: "lang-[request]" */ `./locales/${target}.json`);
      i18n.global.setLocaleMessage(target, mod.default || mod);
      _loaded.add(target);
    } catch (e) {
      console.warn('[i18n] failed to load locale:', target, e);
    }
  }
  i18n.global.locale.value = target;
  try { localStorage.setItem(STORAGE_KEY, target); } catch (_) {}
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('lang', target);
  }
}

export function currentLocale() {
  return i18n.global.locale.value;
}

export default i18n;
